export const MANUAL_UPLOAD_LIMITS = {
  maxFiles: 20,
  fileConcurrency: 1,
  partConcurrency: 3,
  partRetries: 2,
} as const;

export function manualUploadSelectionAllowance(currentCount: number, incomingCount: number) {
  return Math.max(0, Math.min(incomingCount, MANUAL_UPLOAD_LIMITS.maxFiles - currentCount));
}

export type ManualUploadState =
  | "queued"
  | "preparing"
  | "uploading"
  | "verifying"
  | "complete"
  | "failed"
  | "cancelled";

export type ManualUploadTags = {
  personaId: string | null;
  productId: string | null;
  offerId: string | null;
  angleId: string | null;
};

export type QueueUpload = {
  key: string;
  file: File;
  tags: ManualUploadTags;
};

export type UploadUpdate = {
  state?: ManualUploadState;
  transferredBytes?: number;
  error?: string | null;
};

type InitializeResponse = {
  uploadId?: string;
  upload?: { id?: string; transferKind?: "single" | "multipart" };
  mode?: "single" | "multipart";
  uploadUrl?: string;
  url?: string;
  partSizeBytes?: number;
  partSize?: number;
  multipart?: { partSizeBytes?: number } | null;
};

type RequestOptions = { signal?: AbortSignal };

async function jsonRequest<T>(url: string, method: string, body: unknown, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: options.signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { message?: string }).message || `Upload request failed (${response.status})`);
  }
  return data as T;
}

async function cleanupInitializedUpload(baseUrl: string, uploadId: string, stage: string) {
  // Deliberately do not reuse the transfer AbortSignal. Cancellation and
  // disposal abort that signal before this best-effort authenticated DELETE,
  // while the cleanup request must be allowed to finish independently.
  await jsonRequest(`${baseUrl}/${uploadId}`, "DELETE", { stage }).catch(() => undefined);
}

function putWithProgress(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (loaded: number) => void,
  register: (xhr: XMLHttpRequest, active: boolean) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    register(xhr, true);
    xhr.open("PUT", url);
    if (contentType) xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => onProgress(Math.min(body.size, event.loaded));
    xhr.onerror = () => {
      register(xhr, false);
      reject(new Error("Object transfer failed"));
    };
    xhr.onabort = () => {
      register(xhr, false);
      reject(new DOMException("Upload cancelled", "AbortError"));
    };
    xhr.onload = () => {
      register(xhr, false);
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Object transfer failed (${xhr.status})`));
        return;
      }
      onProgress(body.size);
      resolve(xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag") || "");
    };
    xhr.send(body);
  });
}

export class ManualUploadQueue {
  private pending: QueueUpload[] = [];
  private active = 0;
  private stopped = false;
  private controls = new Map<string, { fetch: AbortController; xhr: Set<XMLHttpRequest>; uploadId?: string }>();
  private admitted = new Set<string>();

  constructor(
    private readonly baseUrl: string,
    private readonly onUpdate: (key: string, update: UploadUpdate) => void,
    private readonly onComplete?: (key: string, result: unknown) => void,
  ) {}

  enqueue(items: QueueUpload[]) {
    for (const item of items) {
      if (this.admitted.has(item.key)) continue;
      this.admitted.add(item.key);
      this.pending.push(item);
    }
    this.pump();
  }

  retry(item: QueueUpload) {
    if (this.admitted.has(item.key)) return;
    this.pending = this.pending.filter((entry) => entry.key !== item.key);
    this.onUpdate(item.key, { state: "queued", transferredBytes: 0, error: null });
    this.enqueue([item]);
  }

  async cancel(key: string) {
    const wasPending = this.pending.some((item) => item.key === key);
    this.pending = this.pending.filter((item) => item.key !== key);
    if (wasPending) this.admitted.delete(key);
    const control = this.controls.get(key);
    control?.fetch.abort();
    control?.xhr.forEach((xhr) => xhr.abort());
    this.onUpdate(key, { state: "cancelled", error: null });
    // The active runner performs the authenticated cancel once its abort is
    // observed. If initialization had not returned an id yet, server expiry
    // cleanup remains the safety net.
  }

  dispose() {
    this.stopped = true;
    this.pending = [];
    for (const key of this.controls.keys()) void this.cancel(key);
  }

  private pump() {
    if (this.stopped) return;
    while (this.active < MANUAL_UPLOAD_LIMITS.fileConcurrency && this.pending.length > 0) {
      const item = this.pending.shift()!;
      this.active += 1;
      void this.run(item).finally(() => {
        this.active -= 1;
        this.admitted.delete(item.key);
        this.pump();
      });
    }
  }

  private async run(item: QueueUpload) {
    const control = { fetch: new AbortController(), xhr: new Set<XMLHttpRequest>(), uploadId: undefined as string | undefined };
    let failureStage = "initialization";
    this.controls.set(item.key, control);
    const setXhr = (xhr: XMLHttpRequest, active: boolean) => {
      if (active) control.xhr.add(xhr);
      else control.xhr.delete(xhr);
    };
    try {
      this.onUpdate(item.key, { state: "preparing", transferredBytes: 0, error: null });
      const initialized = await jsonRequest<InitializeResponse>(
        `${this.baseUrl}/init`,
        "POST",
        {
          filename: item.file.name,
          sizeBytes: item.file.size,
          contentType: item.file.type,
          tags: item.tags,
          personaId: item.tags.personaId,
          productId: item.tags.productId,
           offerId: item.tags.offerId,
          angleId: item.tags.angleId,
        },
        { signal: control.fetch.signal },
      );
      const uploadId = initialized.uploadId ?? initialized.upload?.id;
      if (!uploadId) throw new Error("Upload initialization returned no upload id");
      control.uploadId = uploadId;
      failureStage = "object-transfer";
      this.onUpdate(item.key, { state: "uploading" });

      const partSize = initialized.partSizeBytes ?? initialized.partSize ?? initialized.multipart?.partSizeBytes;
      const multipart =
        initialized.mode === "multipart" ||
        initialized.upload?.transferKind === "multipart" ||
        Boolean(initialized.multipart);
      let parts: Array<{ partNumber: number; etag: string }> = [];
      if (multipart) {
        if (!partSize || partSize <= 0) throw new Error("Multipart initialization returned no part size");
        parts = await this.uploadParts(item, uploadId, partSize, control, setXhr);
      } else {
        const uploadUrl = initialized.uploadUrl ?? initialized.url;
        if (!uploadUrl) throw new Error("Upload initialization returned no object URL");
        const etag = await putWithProgress(
          uploadUrl,
          item.file,
          item.file.type,
          (loaded) => this.onUpdate(item.key, { transferredBytes: loaded }),
          setXhr,
        );
        parts = etag ? [{ partNumber: 1, etag }] : [];
      }

      this.onUpdate(item.key, { state: "verifying", transferredBytes: item.file.size });
      failureStage = "verification";
      await jsonRequest(
        `${this.baseUrl}/${uploadId}/complete`,
        "POST",
        { parts, sizeBytes: item.file.size },
        { signal: control.fetch.signal },
      );
      failureStage = "finalization";
      const result = await jsonRequest(
        `${this.baseUrl}/${uploadId}/finalize`,
        "POST",
        { tags: item.tags, ...item.tags },
        { signal: control.fetch.signal },
      );
      this.onUpdate(item.key, { state: "complete", transferredBytes: item.file.size, error: null });
      this.onComplete?.(item.key, result);
    } catch (error) {
      const cancelled = control.fetch.signal.aborted || (error instanceof DOMException && error.name === "AbortError");
      control.fetch.abort();
      control.xhr.forEach((xhr) => xhr.abort());
      this.onUpdate(item.key, {
        state: cancelled ? "cancelled" : "failed",
        error: cancelled ? null : error instanceof Error ? error.message : String(error),
      });
      if (control.uploadId) {
        await cleanupInitializedUpload(
          this.baseUrl,
          control.uploadId,
          cancelled ? "cancellation" : failureStage,
        );
      }
    } finally {
      this.controls.delete(item.key);
    }
  }

  private async uploadParts(
    item: QueueUpload,
    uploadId: string,
    partSize: number,
    control: { fetch: AbortController; xhr: Set<XMLHttpRequest> },
    register: (xhr: XMLHttpRequest, active: boolean) => void,
  ) {
    const count = Math.ceil(item.file.size / partSize);
    const loaded = new Array<number>(count).fill(0);
    const results = new Array<{ partNumber: number; etag: string }>(count);
    let cursor = 0;
    const report = (index: number, bytes: number) => {
      loaded[index] = Math.max(loaded[index], bytes);
      this.onUpdate(item.key, { transferredBytes: Math.min(item.file.size, loaded.reduce((sum, n) => sum + n, 0)) });
    };
    const worker = async () => {
      while (cursor < count) {
        const index = cursor++;
        const partNumber = index + 1;
        const start = index * partSize;
        const blob = item.file.slice(start, Math.min(item.file.size, start + partSize));
        let lastError: unknown;
        for (let attempt = 0; attempt <= MANUAL_UPLOAD_LIMITS.partRetries; attempt++) {
          try {
            const signed = await jsonRequest<{ uploadUrl?: string; url?: string }>(
              `${this.baseUrl}/${uploadId}/parts`,
              "POST",
              { partNumber, sizeBytes: blob.size },
              { signal: control.fetch.signal },
            );
            const url = signed.uploadUrl ?? signed.url;
            if (!url) throw new Error(`Part ${partNumber} returned no object URL`);
            const etag = await putWithProgress(url, blob, item.file.type, (bytes) => report(index, bytes), register);
            results[index] = { partNumber, etag };
            lastError = undefined;
            break;
          } catch (error) {
            lastError = error;
            if (control.fetch.signal.aborted) throw error;
          }
        }
        if (lastError) throw new Error(`Part ${partNumber} failed: ${lastError instanceof Error ? lastError.message : String(lastError)}`);
      }
    };
    await Promise.all(Array.from({ length: Math.min(count, MANUAL_UPLOAD_LIMITS.partConcurrency) }, worker));
    return results;
  }
}